/**
 * Plain-text summaries of a planning result, shared by the information panel, the share card,
 * saved places and the comparison table.
 */
import { fmt } from './ui.js';
import { LEVEL_META } from './config.js';

export const val = (a) => {
  const v = a.value;
  if (typeof v === 'number') return fmt.num(v, 2);
  return String(v);
};
export const attrOf = (cell, key) => cell?.attrs?.find((a) => a.key === key);

const ATTR_HIDE = new Set(['phanLoai', 'loaiDat', 'LoaiDat', 'tenDoiTuong', 'tenDoiTuon', 'maDoiTuong']);

/** Merge min/max pairs into ranges and format values with units. */
export function attrRows(cell, { withArea = true } = {}) {
  const rows = [];
  const used = new Set();
  const pair = (kMin, kMax, label, unit) => {
    const a = attrOf(cell, kMin), b = attrOf(cell, kMax);
    if (!a && !b) return;
    used.add(kMin); used.add(kMax);
    const u = unit ? (unit === '%' ? '%' : ' ' + unit) : '';
    const v = a && b ? (val(a) === val(b) ? val(a) : `${val(a)} – ${val(b)}`) : b ? `≤ ${val(b)}` : `≥ ${val(a)}`;
    rows.push([label, v + u]);
  };
  for (const a of cell.attrs || []) {
    if (ATTR_HIDE.has(a.key) || used.has(a.key)) continue;
    if (a.key === 'tangCaoMin' || a.key === 'tangCaoMax') { pair('tangCaoMin', 'tangCaoMax', 'Tầng cao', 'tầng'); continue; }
    if (a.key === 'matDoXayDungMin' || a.key === 'matDoXayDungMax') { pair('matDoXayDungMin', 'matDoXayDungMax', 'Mật độ xây dựng', '%'); continue; }
    const u = a.unit ? (a.unit === '%' ? '%' : ' ' + a.unit) : '';
    rows.push([a.label, val(a) + u]);
  }
  if (withArea && Number.isFinite(cell.areaComputed)) rows.push(['Diện tích tính theo VN-2000', `${fmt.num(cell.areaComputed, 1)} m²`]);
  return rows;
}

export function levelLabel(level) {
  const p = level.project;
  const scale = p?.scale ? ` · ${p.scale}` : '';
  if (level.key === 'zone') return `Quy hoạch phân khu${p?.code ? ' ' + p.code : ''}${scale}`;
  if (level.key === 'detail') return `Quy hoạch chi tiết${p?.code ? ' ' + p.code : ''} · 1/500`;
  return 'Quy hoạch chung Thủ đô';
}

/** The key figures of a cell as text: area, floors, building density, land-use coefficient. */
export function keyFigures(cell) {
  if (!cell) return {};
  const out = {};
  const area = attrOf(cell, 'dienTich_m2') || attrOf(cell, 'Dtich_m2');
  const ha = attrOf(cell, 'dienTich_ha');
  if (area && Number.isFinite(+area.value)) out.area = fmt.area(+area.value);
  else if (ha && Number.isFinite(+ha.value)) out.area = `${fmt.num(+ha.value, 2)} ha`;
  else if (Number.isFinite(cell.areaComputed)) out.area = fmt.area(cell.areaComputed);
  const tMin = attrOf(cell, 'tangCaoMin'), tMax = attrOf(cell, 'tangCaoMax');
  if (tMin || tMax) out.floors = `${tMin && tMax && val(tMin) !== val(tMax) ? `${val(tMin)}–${val(tMax)}` : val(tMax || tMin)} tầng`;
  const dMax = attrOf(cell, 'matDoXayDungMax'), dMin = attrOf(cell, 'matDoXayDungMin');
  if (dMax || dMin) out.density = `${dMax ? '≤ ' + val(dMax) : '≥ ' + val(dMin)}%`;
  const hs = attrOf(cell, 'heSoSDD');
  if (hs) out.far = `${val(hs)} lần`;
  const ds = attrOf(cell, 'danSo') || attrOf(cell, 'quyMoDanSo');
  if (ds) out.population = `${val(ds)} người`;
  return out;
}

export const FIGURE_LABELS = { area: 'Diện tích ô đất', floors: 'Tầng cao', density: 'Mật độ xây dựng', far: 'Hệ số sử dụng đất', population: 'Dân số' };

/** One planning level as a compact record (also stored with saved places). */
export function levelInfo(level) {
  if (!level) return null;
  const c = level.cells?.[0];
  const p = level.project;
  return {
    key: level.key,
    title: LEVEL_META[level.key]?.title || level.title,
    label: levelLabel(level),
    fn: c ? c.function || c.category || c.name || 'Ô đất quy hoạch' : null,
    category: c?.category || null,
    code: c?.code || null,
    color: c?.color?.hex || null,
    figures: keyFigures(c),
    zoning: (level.zoning || []).map((z) => z.category || z.name).filter(Boolean),
    project: p ? { code: p.code || null, name: p.name || null, decision: p.decision || null, date: p.decisionDate || null, status: p.status || null } : null,
  };
}

const ORDER = ['detail', 'zone', 'general'];
/** The most detailed level with a cell at the point. */
export function primaryLevel(result) {
  const levels = result?.levels || [];
  for (const k of ORDER) { const l = levels.find((x) => x.key === k && x.cells?.length); if (l) return l; }
  return null;
}

/** Everything the share card / saved places need about the current selection. */
export function placeInfo(s) {
  if (!s) return null;
  const a = s.address;
  const p = primaryLevel(s.result);
  return {
    lat: s.lat, lng: s.lng,
    title: a?.title || 'Vị trí đã chọn',
    sub: a?.sub || '',
    primary: levelInfo(p),
    levels: (s.result?.levels || []).map(levelInfo),
    qh100: s.qh100?.hit ? { category: s.qh100.hit.category || s.qh100.hit.name || null, color: s.qh100.hit.color?.hex || null } : null,
    loaded: !!s.result,
  };
}

/** One-line description: "Ô CQ2 · QH phân khu H1.1B · 1/2000 · Tầng cao 6 tầng · Mật độ ≤ 70%". */
export function describe(info) {
  if (!info) return '';
  const p = info.primary;
  if (!p) return info.loaded ? 'Chưa có dữ liệu quy hoạch số hoá tại vị trí này.' : '';
  const bits = [];
  if (p.code) bits.push(`Ô ${p.code}`);
  bits.push(p.label);
  const f = p.figures;
  if (f.area) bits.push(`Diện tích ${f.area}`);
  if (f.floors) bits.push(`Tầng cao ${f.floors}`);
  if (f.density) bits.push(`Mật độ XD ${f.density}`);
  if (f.far) bits.push(`Hệ số SDĐ ${f.far}`);
  return bits.join(' · ');
}
